import { useEffect, useState, useMemo } from 'react';
import { useOne, useList, useCustom, useCustomMutation, useGo } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { notify } from '@/lib/notify';
import {
  Loader2,
  Info,
  MessageSquare,
  History,
  StickyNote,
  User,
  UserCheck,
  Calendar,
  ArrowRight,
  Tag,
  X,
  Send,
  Trash2,
  Edit2,
  Clock,
  PlusCircle,
  ShoppingCart,
  Link2,
  ExternalLink,
} from 'lucide-react';
import { OrderTargetType } from '@masters-club/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { FormHeader } from '@/components/forms';
import { EntityAnalyticsCard, EntityAnalyticsGrid, TwoColumnLayout } from '@/components/shared';
import {
  getLeadDisplayName,
  type Lead,
  type LeadNote,
  type LeadStatusConfig,
  type AdminUser,
} from './types';
import type { TagItem } from '../tags/types';
import { TagPickerWithCreate } from '../tags/components';

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface UserOption {
  id: number;
  telegramId?: string | null;
  username?: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

interface TranslationsByLang {
  ru?: { name?: string | null; title?: string | null };
  [lang: string]: { name?: string | null; title?: string | null } | undefined;
}

interface TranslationsArray {
  languageCode: string;
  name?: string | null;
  title?: string | null;
  data?: { name?: string; title?: string };
}

interface CourseOption {
  id: number;
  slug: string;
  translations?: Array<TranslationsArray & { title?: string | null }>;
}

interface SubscriptionPlanOption {
  id: number;
  code: string;
  translations?: TranslationsByLang;
}

interface ChannelPassOption {
  id: number;
  translations?: TranslationsByLang;
  channel?: { title: string } | null;
}

interface PricingTierOption {
  id: number;
  courseId: number | null;
  subscriptionPlanId: number | null;
  channelPassId: number | null;
  translations?: TranslationsByLang | Array<TranslationsArray>;
}

interface CreatedOrderResult {
  id: number;
}

const TARGET_TYPE_OPTIONS: Array<{ value: OrderTargetType; label: string }> = [
  { value: OrderTargetType.COURSE, label: 'Курс' },
  { value: OrderTargetType.SUBSCRIPTION_PLAN, label: 'Абонемент' },
  { value: OrderTargetType.CHANNEL_PASS, label: 'Доступ к каналу' },
];

function isOrderTargetType(value: string): value is OrderTargetType {
  return TARGET_TYPE_OPTIONS.some((option) => option.value === value);
}

function getUserOptionLabel(user: UserOption): string {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  if (fullName) { return fullName; }
  if (user.username) { return `@${user.username}`; }
  if (user.email) { return user.email; }
  return `Пользователь #${user.id}`;
}

function getLeadUserLabel(user: Lead['user']): string {
  if (!user) { return 'Не привязан'; }
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  if (fullName) { return fullName; }
  if (user.username) { return `@${user.username}`; }
  if (user.email) { return user.email; }
  return `Пользователь #${user.id}`;
}

function pickAnyTranslationField(
  translations: TranslationsByLang | Array<TranslationsArray> | undefined,
  field: 'name' | 'title',
): string | null {
  if (!translations) { return null; }
  const entries = Array.isArray(translations)
    ? translations.map((t) => ({ value: t[field] ?? t.data?.[field] }))
    : Object.values(translations).map((t) => ({ value: t?.[field] }));
  for (const e of entries) {
    if (e.value) { return e.value; }
  }
  return null;
}

function resolveCourseLabel(item: CourseOption): string {
  const ruTranslation = (item.translations ?? []).find((t) => t.languageCode === 'ru');
  const ruTitle = ruTranslation?.title ?? ruTranslation?.data?.title;
  return ruTitle || pickAnyTranslationField(item.translations, 'title') || item.slug || `#${item.id}`;
}

function resolveSubscriptionPlanLabel(item: SubscriptionPlanOption): string {
  return item.translations?.ru?.name || pickAnyTranslationField(item.translations, 'name') || item.code || `#${item.id}`;
}

function resolveChannelPassLabel(item: ChannelPassOption): string {
  return item.translations?.ru?.name || pickAnyTranslationField(item.translations, 'name') || item.channel?.title || `#${item.id}`;
}

function resolvePricingTierLabel(item: PricingTierOption): string {
  const translations = item.translations;
  if (Array.isArray(translations)) {
    const ru = translations.find((t) => t.languageCode === 'ru');
    const ruName = ru?.name ?? ru?.data?.name;
    return ruName ?? pickAnyTranslationField(translations, 'name') ?? `#${item.id}`;
  }
  return translations?.ru?.name ?? pickAnyTranslationField(translations, 'name') ?? `#${item.id}`;
}

function UserSelect({
  value,
  onChange,
  search,
  onSearchChange,
  disabled,
}: {
  value: number | undefined;
  onChange: (id: number | undefined) => void;
  search: string;
  onSearchChange: (value: string) => void;
  disabled?: boolean;
}) {
  const usersResult = useList<UserOption>({
    resource: 'users',
    pagination: { pageSize: 50 },
    filters: search.trim()
      ? [{ field: 'search', operator: 'contains', value: search.trim() }]
      : [],
    queryOptions: { staleTime: 30_000 },
  });

  const users = usersResult.result?.data ?? [];
  const isLoading = usersResult.query?.isLoading ?? false;

  return (
    <div className="space-y-2">
      <Input
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Поиск по имени, email или Telegram"
        disabled={disabled}
      />
      <Select
        value={value !== undefined ? String(value) : undefined}
        onValueChange={(next) => onChange(Number(next))}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue placeholder={isLoading ? 'Загрузка...' : 'Выберите пользователя'} />
        </SelectTrigger>
        <SelectContent>
          {users.map((user) => (
            <SelectItem key={user.id} value={String(user.id)}>
              {getUserOptionLabel(user)}
              {user.email ? ` · ${user.email}` : ''}
            </SelectItem>
          ))}
          {!isLoading && users.length === 0 && (
            <SelectItem value="__empty" disabled>
              Пользователи не найдены
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

function ProductSelect({
  targetType,
  value,
  onChange,
}: {
  targetType: OrderTargetType;
  value: number | undefined;
  onChange: (id: number | undefined) => void;
}) {
  const isCourse = targetType === OrderTargetType.COURSE;
  const isPlan = targetType === OrderTargetType.SUBSCRIPTION_PLAN;
  const isPass = targetType === OrderTargetType.CHANNEL_PASS;

  const coursesResult = useList<CourseOption>({
    resource: 'courses',
    pagination: { pageSize: 200 },
    queryOptions: { enabled: isCourse, staleTime: 60_000 },
  });
  const plansResult = useList<SubscriptionPlanOption>({
    resource: 'subscription-plans',
    pagination: { pageSize: 200 },
    queryOptions: { enabled: isPlan, staleTime: 60_000 },
  });
  const passesResult = useList<ChannelPassOption>({
    resource: 'channel-passes',
    pagination: { pageSize: 200 },
    queryOptions: { enabled: isPass, staleTime: 60_000 },
  });

  const options = useMemo(() => {
    if (isCourse) {
      return (coursesResult.result?.data ?? []).map((course) => ({
        id: course.id,
        label: resolveCourseLabel(course),
      }));
    }
    if (isPlan) {
      return (plansResult.result?.data ?? []).map((plan) => ({
        id: plan.id,
        label: resolveSubscriptionPlanLabel(plan),
      }));
    }
    if (isPass) {
      return (passesResult.result?.data ?? []).map((pass) => ({
        id: pass.id,
        label: resolveChannelPassLabel(pass),
      }));
    }
    return [];
  }, [isCourse, isPlan, isPass, coursesResult.result, plansResult.result, passesResult.result]);

  const isLoading =
    (isCourse && coursesResult.query?.isLoading) ||
    (isPlan && plansResult.query?.isLoading) ||
    (isPass && passesResult.query?.isLoading);

  return (
    <Select value={value !== undefined ? String(value) : undefined} onValueChange={(next) => onChange(Number(next))}>
      <SelectTrigger>
        <SelectValue placeholder={isLoading ? 'Загрузка...' : 'Выберите товар'} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.id} value={String(option.id)}>
            {option.label}
          </SelectItem>
        ))}
        {!isLoading && options.length === 0 && (
          <SelectItem value="__empty" disabled>
            Нет доступных товаров
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

function PricingTierSelect({
  targetType,
  targetId,
  value,
  onChange,
}: {
  targetType: OrderTargetType;
  targetId: number | undefined;
  value: number | undefined;
  onChange: (id: number | undefined) => void;
}) {
  const { result, query } = useList<PricingTierOption>({
    resource: 'pricing-tiers',
    pagination: { pageSize: 200 },
    queryOptions: { staleTime: 60_000 },
  });

  const tiersData = result?.data;
  const filteredTiers = useMemo(() => (tiersData ?? []).filter((tier) => {
    if (targetType === OrderTargetType.COURSE) {
      return tier.courseId !== null && (targetId === undefined || tier.courseId === targetId);
    }
    if (targetType === OrderTargetType.SUBSCRIPTION_PLAN) {
      return tier.subscriptionPlanId !== null && (targetId === undefined || tier.subscriptionPlanId === targetId);
    }
    if (targetType === OrderTargetType.CHANNEL_PASS) {
      return tier.channelPassId !== null && (targetId === undefined || tier.channelPassId === targetId);
    }
    return false;
  }), [tiersData, targetType, targetId]);

  return (
    <Select
      value={value !== undefined ? String(value) : '__none__'}
      onValueChange={(next) => onChange(next === '__none__' ? undefined : Number(next))}
    >
      <SelectTrigger>
        <SelectValue placeholder={query?.isLoading ? 'Загрузка...' : 'Без тарифа'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">Без тарифа</SelectItem>
        {filteredTiers.map((tier) => (
          <SelectItem key={tier.id} value={String(tier.id)}>
            {resolvePricingTierLabel(tier)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---- Lead Notes ----

interface LeadNotesProps {
  leadId: number;
}

function LeadNotes({ leadId }: LeadNotesProps) {
  const [newNote, setNewNote] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');

  const notesQuery = useCustom<LeadNote[]>({
    url: `/leads/${leadId}/notes`,
    method: 'get',
  });

  const isLoading = notesQuery.query?.isLoading ?? false;
  const refetch = notesQuery.query?.refetch;
  const notes = notesQuery.query?.data?.data ?? [];

  const createMutation = useCustomMutation();
  const updateMutation = useCustomMutation();
  const deleteMutation = useCustomMutation();

  const handleCreate = () => {
    if (!newNote.trim()) { return; }
    createMutation.mutate(
      { url: `/leads/${leadId}/notes`, method: 'post', values: { content: newNote } },
      { onSuccess: () => { setNewNote(''); refetch?.(); } },
    );
  };

  const handleUpdate = (noteId: number) => {
    if (!editContent.trim()) { return; }
    updateMutation.mutate(
      { url: `/leads/${leadId}/notes/${noteId}`, method: 'patch', values: { content: editContent } },
      { onSuccess: () => { setEditingId(null); setEditContent(''); refetch?.(); } },
    );
  };

  const handleDelete = (noteId: number) => {
    deleteMutation.mutate(
      { url: `/leads/${leadId}/notes/${noteId}`, method: 'delete', values: {} },
      { onSuccess: () => refetch?.() },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        <Textarea
          placeholder="Добавить заметку..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          className="min-h-[56px] resize-none text-sm"
        />
        <Button
          size="icon"
          className="h-8 w-8 shrink-0 self-start"
          onClick={handleCreate}
          disabled={!newNote.trim() || createMutation.mutation.isPending}
        >
          {createMutation.mutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>

      <ScrollArea className="h-[280px]">
        <div className="space-y-1.5 pr-3">
          {notes.length === 0 && (
            <div className="text-center text-muted-foreground py-4 text-sm">Нет заметок</div>
          )}
          {notes.map((note) => (
            <div key={note.id} className="rounded border px-2.5 py-1.5 space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium">{note.adminUser?.username ?? 'Неизвестно'}</span>
                  <span className="text-[11px] text-muted-foreground">{formatDateTime(note.createdAt)}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => { setEditingId(note.id); setEditContent(note.content); }}
                  >
                    <Edit2 className="h-2.5 w-2.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(note.id)}
                    disabled={deleteMutation.mutation.isPending}
                  >
                    <Trash2 className="h-2.5 w-2.5" />
                  </Button>
                </div>
              </div>

              {editingId === note.id ? (
                <div className="flex gap-1.5">
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="min-h-[48px] resize-none text-sm"
                  />
                  <div className="flex flex-col gap-0.5">
                    <Button size="sm" className="h-7 text-xs" onClick={() => handleUpdate(note.id)} disabled={updateMutation.mutation.isPending}>
                      {updateMutation.mutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'OK'}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingId(null)}>Нет</Button>
                  </div>
                </div>
              ) : (
                <p className="text-xs whitespace-pre-wrap">{note.content}</p>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

// ---- Lead History ----

interface HistoryItem {
  id: number;
  leadId: number;
  adminUserId: number | null;
  action: string;
  previousValue: unknown;
  newValue: unknown;
  fromStatusId: number | null;
  toStatusId: number | null;
  createdAt: string;
  adminUser?: { id: number; username: string } | null;
  fromStatus?: { id: number; name: string; color: string } | null;
  toStatus?: { id: number; name: string; color: string } | null;
}

function getHistoryActionLabel(action: string): string {
  const labels: Record<string, string> = {
    status_changed: 'Изменение статуса',
    note_added: 'Добавлена заметка',
    note_updated: 'Обновлена заметка',
    note_deleted: 'Удалена заметка',
    assigned: 'Назначен ответственный',
    unassigned: 'Снят ответственный',
    created: 'Лид создан',
    field_updated: 'Обновлено поле',
    tag_added: 'Добавлен тег',
    tag_removed: 'Удалён тег',
    converted_to_user: 'Лид конвертирован',
  };
  return labels[action] ?? action;
}

interface LeadHistoryProps {
  leadId: number;
}

function LeadHistory({ leadId }: LeadHistoryProps) {
  const historyQuery = useCustom<HistoryItem[]>({
    url: `/leads/${leadId}/history`,
    method: 'get',
  });

  const isLoading = historyQuery.query?.isLoading ?? false;
  const history = historyQuery.query?.data?.data ?? [];

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
      </div>
    );
  }

  if (history.length === 0) {
    return <div className="text-center text-muted-foreground py-4 text-sm">История пуста</div>;
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'status_changed': return <ArrowRight className="h-2.5 w-2.5" />;
      case 'note_added':
      case 'note_updated':
      case 'note_deleted': return <MessageSquare className="h-2.5 w-2.5" />;
      case 'created': return <PlusCircle className="h-2.5 w-2.5" />;
      case 'converted_to_user': return <UserCheck className="h-2.5 w-2.5" />;
      case 'tag_added':
      case 'tag_removed': return <Tag className="h-2.5 w-2.5" />;
      default: return <Clock className="h-2.5 w-2.5" />;
    }
  };

  return (
    <ScrollArea className="h-[320px]">
      <div className="space-y-0 pr-3">
        {history.map((item) => (
          <div key={item.id} className="flex gap-2 border-l-2 border-muted pl-3 pb-2.5 relative">
            <div className="absolute -left-[7px] top-0.5 w-3 h-3 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
              {getActionIcon(item.action)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium text-xs">{getHistoryActionLabel(item.action)}</span>
                <span className="text-[11px] text-muted-foreground">{formatDateTime(item.createdAt)}</span>
                {item.adminUser && (
                  <span className="text-[11px] text-muted-foreground">({item.adminUser.username})</span>
                )}
              </div>
              {item.action === 'status_changed' && item.fromStatus && item.toStatus && (
                <div className="flex items-center gap-1 mt-0.5">
                  <Badge variant="outline" className="text-[11px] px-1.5 py-0" style={{ borderColor: item.fromStatus.color }}>
                    {item.fromStatus.name}
                  </Badge>
                  <ArrowRight className="h-2.5 w-2.5 text-muted-foreground" />
                  <Badge variant="outline" className="text-[11px] px-1.5 py-0" style={{ borderColor: item.toStatus.color }}>
                    {item.toStatus.name}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

function LinkLeadUserDialog({
  lead,
  open,
  onOpenChange,
  onLinked,
}: {
  lead: Lead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLinked: () => void;
}) {
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(lead.user?.id ?? undefined);
  const linkMutation = useCustomMutation();

  useEffect(() => {
    if (open) {
      setSelectedUserId(lead.user?.id ?? undefined);
      setSearch('');
    }
  }, [lead.user?.id, open]);

  const handleSubmit = () => {
    if (selectedUserId === undefined) { return; }
    linkMutation.mutate(
      {
        url: `/leads/${lead.id}/link-user`,
        method: 'post',
        values: { userId: selectedUserId },
      },
      {
        onSuccess: () => {
          notify.success('Пользователь привязан к лиду');
          onLinked();
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Привязать пользователя</DialogTitle>
          <DialogDescription>
            Связь будет сохранена у лида и появится на странице пользователя.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Пользователь</Label>
          <UserSelect
            value={selectedUserId}
            onChange={setSelectedUserId}
            search={search}
            onSearchChange={setSearch}
            disabled={linkMutation.mutation.isPending}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedUserId === undefined || linkMutation.mutation.isPending}
          >
            {linkMutation.mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Привязать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateOrderFromLeadDialog({
  lead,
  open,
  onOpenChange,
  onCreated,
}: {
  lead: Lead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (orderId: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(lead.user?.id ?? undefined);
  const [targetType, setTargetType] = useState<OrderTargetType>(OrderTargetType.COURSE);
  const [targetId, setTargetId] = useState<number | undefined>();
  const [pricingTierId, setPricingTierId] = useState<number | undefined>();
  const [quantity, setQuantity] = useState(1);
  const createMutation = useCustomMutation<CreatedOrderResult>();

  useEffect(() => {
    if (open) {
      setSelectedUserId(lead.user?.id ?? undefined);
      setSearch('');
      setTargetType(OrderTargetType.COURSE);
      setTargetId(undefined);
      setPricingTierId(undefined);
      setQuantity(1);
    }
  }, [lead.user?.id, open]);

  const handleTargetTypeChange = (next: OrderTargetType) => {
    setTargetType(next);
    setTargetId(undefined);
    setPricingTierId(undefined);
  };

  const handleSubmit = () => {
    if (selectedUserId === undefined || targetId === undefined) { return; }
    // Не плодить дубль заказа при повторном/двойном submit, пока мутация в полёте.
    if (createMutation.mutation.isPending) { return; }

    createMutation.mutate(
      {
        url: `/leads/${lead.id}/create-order`,
        method: 'post',
        values: {
          userId: selectedUserId,
          items: [
            {
              targetType,
              targetId,
              quantity,
              ...(pricingTierId !== undefined ? { pricingTierId } : {}),
            },
          ],
        },
      },
      {
        onSuccess: (response) => {
          notify.success('Заказ создан из лида');
          onOpenChange(false);
          onCreated(response.data.id);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Создать заказ из лида</DialogTitle>
          <DialogDescription>
            Заказ создаётся через общий commerce-поток с теми же товарами, тарифами и проверками.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="space-y-2">
            <Label>Пользователь</Label>
            {lead.user ? (
              <div className="rounded-md border px-3 py-2 text-sm">
                {getLeadUserLabel(lead.user)}
                {lead.user.email ? <span className="text-muted-foreground"> · {lead.user.email}</span> : null}
              </div>
            ) : (
              <UserSelect
                value={selectedUserId}
                onChange={setSelectedUserId}
                search={search}
                onSearchChange={setSearch}
                disabled={createMutation.mutation.isPending}
              />
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Тип товара</Label>
              <Select
                value={targetType}
                onValueChange={(next) => {
                  if (isOrderTargetType(next)) {
                    handleTargetTypeChange(next);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TARGET_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Количество</Label>
              <Input
                type="number"
                min={1}
                value={quantity}
                onChange={(event) => setQuantity(Math.max(1, Number(event.target.value) || 1))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Товар</Label>
            <ProductSelect
              targetType={targetType}
              value={targetId}
              onChange={(next) => {
                setTargetId(next);
                setPricingTierId(undefined);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>Тариф</Label>
            <PricingTierSelect
              targetType={targetType}
              targetId={targetId}
              value={pricingTierId}
              onChange={setPricingTierId}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedUserId === undefined || targetId === undefined || createMutation.mutation.isPending}
          >
            {createMutation.mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Создать заказ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadCrmPanel({
  lead,
  allStatuses,
  admins,
  onRefetch,
  onLinkUser,
  onCreateOrder,
}: {
  lead: Lead;
  allStatuses: LeadStatusConfig[];
  admins: AdminUser[];
  onRefetch: () => void;
  onLinkUser: () => void;
  onCreateOrder: () => void;
}) {
  const go = useGo();
  const changeStatusMutation = useCustomMutation();
  const assignMutation = useCustomMutation();
  const assignedName = lead.assignedAdminId
    ? admins.find((admin) => admin.id === lead.assignedAdminId)?.username ?? `Админ #${lead.assignedAdminId}`
    : 'Не назначен';

  const handleStatusChange = (value: string) => {
    changeStatusMutation.mutate(
      { url: `/leads/${lead.id}/change-status`, method: 'post', values: { statusConfigId: Number(value) } },
      { onSuccess: onRefetch },
    );
  };

  const handleAssignChange = (value: string) => {
    const adminId = value === 'none' ? null : Number(value);
    assignMutation.mutate(
      { url: `/leads/${lead.id}`, method: 'patch', values: { assignedAdminId: adminId } },
      { onSuccess: onRefetch },
    );
  };

  return (
    <div className="space-y-3">
      <EntityAnalyticsGrid>
        <EntityAnalyticsCard
          title="Статус"
          value={lead.statusConfig?.name ?? 'Не задан'}
          icon={<Info className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Назначен"
          value={assignedName}
          icon={<UserCheck className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Пользователь"
          value={getLeadUserLabel(lead.user)}
          icon={<User className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Создан"
          value={formatDate(lead.createdAt)}
          icon={<Calendar className="h-4 w-4" />}
        />
      </EntityAnalyticsGrid>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(260px,340px)]">
        <Card>
          <CardHeader className="p-3 pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ArrowRight className="h-3.5 w-3.5" />
              Статус лида
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {lead.statusConfig && (
                <Badge
                  variant="outline"
                  className="w-fit px-3 py-1.5 text-sm"
                  style={{ borderColor: lead.statusConfig.color, color: lead.statusConfig.color }}
                >
                  {lead.statusConfig.name}
                </Badge>
              )}
              <Select value={String(lead.statusConfigId)} onValueChange={handleStatusChange}>
                <SelectTrigger className="h-9 sm:max-w-xs">
                  <SelectValue placeholder="Выберите статус" />
                </SelectTrigger>
                <SelectContent>
                  {allStatuses.map((status) => (
                    <SelectItem key={status.id} value={String(status.id)}>
                      {status.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-3 pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <UserCheck className="h-3.5 w-3.5" />
              Действия
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 space-y-2">
            <Select
              value={lead.assignedAdminId ? String(lead.assignedAdminId) : 'none'}
              onValueChange={handleAssignChange}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Менеджер" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Не назначен</SelectItem>
                {admins.map((admin) => (
                  <SelectItem key={admin.id} value={String(admin.id)}>
                    {admin.username} ({admin.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1">
              <Button type="button" variant="outline" onClick={onLinkUser}>
                <Link2 className="mr-2 h-4 w-4" />
                {lead.user ? 'Сменить пользователя' : 'Привязать пользователя'}
              </Button>
              <Button type="button" onClick={onCreateOrder}>
                <ShoppingCart className="mr-2 h-4 w-4" />
                Создать заказ
              </Button>
            </div>

            {lead.user && (
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start px-2"
                onClick={() => go({ to: `/users/show/${lead.user?.id}` })}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Открыть пользователя
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---- Overview Tab ----

function OverviewTab({
  lead,
  tags,
  onRefetch,
}: {
  lead: Lead;
  tags: TagItem[];
  onRefetch: () => void;
}) {
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const addTagMutation = useCustomMutation();
  const removeTagMutation = useCustomMutation();

  const handleAddTag = (tagId: number) => {
    addTagMutation.mutate(
      { url: `/leads/${lead.id}/tags`, method: 'post', values: { tagId } },
      {
        onSuccess: () => {
          setSelectedTagId(null);
          onRefetch();
        },
      },
    );
  };

  const handleRemoveTag = (tagId: number) => {
    removeTagMutation.mutate(
      { url: `/leads/${lead.id}/tags/${tagId}`, method: 'delete', values: {} },
      { onSuccess: onRefetch },
    );
  };

  const existingTagIds = new Set(lead.tags.map((t) => t.tagId));
  const availableTagsToAdd = tags.filter((t) => !existingTagIds.has(t.id));

  return (
    <div className="space-y-3">
      <TwoColumnLayout
        gap="sm"
        main={
          <div className="space-y-3">
            {/* Contact info */}
            <Card>
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-sm">Контактные данные</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-2">
                {[
                  { label: 'Email', value: lead.email },
                  { label: 'Телефон', value: lead.phone },
                  { label: 'Telegram', value: lead.telegramUsername ? `@${lead.telegramUsername}` : null },
                  { label: 'Имя', value: lead.firstName },
                  { label: 'Фамилия', value: lead.lastName },
                ].map(({ label, value }) => value ? (
                  <div key={label} className="flex items-start gap-2 text-sm">
                    <span className="text-muted-foreground min-w-[80px]">{label}</span>
                    <span className="font-medium break-all">{value}</span>
                  </div>
                ) : null)}
                {!lead.email && !lead.phone && !lead.telegramUsername && !lead.firstName && !lead.lastName && (
                  <p className="text-sm text-muted-foreground">Нет данных</p>
                )}
              </CardContent>
            </Card>

            {/* Tags */}
            <Card>
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Tag className="h-3.5 w-3.5" />
                  Теги
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-2">
                <div className="flex flex-wrap gap-1">
                  {lead.tags.map((t) => (
                    <Badge
                      key={t.id}
                      variant="outline"
                      className="text-xs px-2 py-0.5"
                      style={{ borderColor: t.tag.color, color: t.tag.color }}
                    >
                      {t.tag.name}
                      <button
                        type="button"
                        className="ml-1 hover:opacity-70"
                        onClick={() => handleRemoveTag(t.tagId)}
                        disabled={removeTagMutation.mutation.isPending}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </Badge>
                  ))}
                  {lead.tags.length === 0 && (
                    <span className="text-sm text-muted-foreground">Нет тегов</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <TagPickerWithCreate
                      tags={availableTagsToAdd}
                      value={selectedTagId}
                      onChange={(tagId) => setSelectedTagId(tagId)}
                      onCreated={(tag) => handleAddTag(tag.id)}
                      allowClear={false}
                      placeholder="Добавить тег"
                      disabled={addTagMutation.mutation.isPending}
                      createDialogTitle="Создать и назначить тег"
                      createDialogDescription="Новый тег сразу будет назначен этому лиду."
                      createSuccessMessage="Тег создан"
                    />
                  </div>
                  {availableTagsToAdd.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={selectedTagId === null || addTagMutation.mutation.isPending}
                      onClick={() => {
                        if (selectedTagId !== null) {
                          handleAddTag(selectedTagId);
                        }
                      }}
                    >
                      Добавить
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Custom data */}
            {lead.customData && Object.keys(lead.customData).length > 0 && (
              <Card>
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="text-sm">Кастомные данные</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-1">
                  {Object.entries(lead.customData).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-2 text-sm">
                      <span className="text-muted-foreground min-w-[120px] font-mono text-xs">{key}</span>
                      <span className="break-all text-xs">{String(value)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        }
        sidebar={
          <div className="space-y-3">
            {/* Meta info */}
            <Card>
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-sm">Информация</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Создан</span>
                  <span>{formatDate(lead.createdAt)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Обновлён</span>
                  <span>{formatDate(lead.updatedAt)}</span>
                </div>
                {lead.deletedAt && (
                  <>
                    <Separator />
                    <div className="flex justify-between text-xs text-destructive">
                      <span>Удалён</span>
                      <span>{formatDate(lead.deletedAt)}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        }
      />
    </div>
  );
}

// ---- Main Show Component ----

export function LeadsShow() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('overview');
  const [linkUserOpen, setLinkUserOpen] = useState(false);
  const [createOrderOpen, setCreateOrderOpen] = useState(false);

  const leadResult = useOne<Lead>({
    resource: 'leads',
    id: Number(id),
  });

  const lead = leadResult.result;
  const isLoading = leadResult.query?.isLoading ?? false;
  const refetchLead = leadResult.query?.refetch;

  const adminsResult = useList<AdminUser>({
    resource: 'admin-users',
    pagination: { mode: 'off' },
  });
  const admins = useMemo(() => adminsResult.result?.data ?? [], [adminsResult.result?.data]);

  const statusesResult = useCustom<LeadStatusConfig[]>({
    url: '/leads/statuses',
    method: 'get',
  });
  const allStatuses = statusesResult.query?.data?.data ?? [];

  const tagsResult = useList<TagItem>({
    resource: 'tags',
    pagination: { mode: 'off' },
  });
  const tags = tagsResult.result?.data ?? [];

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Лид не найден</p>
        <Button variant="link" onClick={() => go({ to: '/leads' })}>Вернуться к списку</Button>
      </div>
    );
  }

  const displayName = getLeadDisplayName(lead);
  const statusBadge = lead.statusConfig ? (
    <Badge
      variant="outline"
      style={{ borderColor: lead.statusConfig.color, color: lead.statusConfig.color }}
    >
      {lead.statusConfig.name}
    </Badge>
  ) : (
    <Badge variant="outline">Без статуса</Badge>
  );

  return (
    <div className="space-y-3">
      <FormHeader
        title={displayName}
        description={`Лид #${lead.id} • ${formatDate(lead.createdAt)}`}
        onBack={() => go({ to: '/leads' })}
        titleAddon={statusBadge}
        icon={<User className="h-5 w-5" />}
        sticky
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
        <TabsList className="grid w-full grid-cols-3 lg:inline-flex lg:w-auto">
          <TabsTrigger value="overview" className="flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Обзор</span>
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-1.5">
            <StickyNote className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Заметки</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">История</span>
          </TabsTrigger>
        </TabsList>

        <LeadCrmPanel
          lead={lead}
          allStatuses={allStatuses}
          admins={admins}
          onRefetch={() => refetchLead?.()}
          onLinkUser={() => setLinkUserOpen(true)}
          onCreateOrder={() => setCreateOrderOpen(true)}
        />

        <TabsContent value="overview" forceMount className="data-[state=inactive]:hidden">
          <OverviewTab
            lead={lead}
            tags={tags}
            onRefetch={() => refetchLead?.()}
          />
        </TabsContent>

        <TabsContent value="notes" forceMount className="data-[state=inactive]:hidden">
          <LeadNotes leadId={lead.id} />
        </TabsContent>

        <TabsContent value="history" forceMount className="data-[state=inactive]:hidden">
          <LeadHistory leadId={lead.id} />
        </TabsContent>
      </Tabs>

      <LinkLeadUserDialog
        lead={lead}
        open={linkUserOpen}
        onOpenChange={setLinkUserOpen}
        onLinked={() => refetchLead?.()}
      />

      <CreateOrderFromLeadDialog
        lead={lead}
        open={createOrderOpen}
        onOpenChange={setCreateOrderOpen}
        onCreated={(orderId) => {
          void refetchLead?.();
          go({ to: `/orders/show/${orderId}` });
        }}
      />
    </div>
  );
}
